// src/pages/services/GameDevelopment.jsx - Premium Game Development Solutions
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Gamepad2, Smartphone, Monitor, Globe, Zap, Users, ExternalLink } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function GameDevelopment() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <Smartphone className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Mobile Game Development',
      description: 'Engaging mobile games for iOS and Android',
      features: ['Casual Games', 'Hyper-casual', 'Multiplayer', 'In-app Purchases']
    },
    {
      icon: <Monitor className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'PC/Console Games',
      description: 'High-quality games for desktop and console',
      features: ['3D Graphics', 'Advanced AI', 'Story-driven', 'Action/Adventure']
    },
    {
      icon: <Globe className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Web-based Games',
      description: 'Browser games with HTML5 and WebGL',
      features: ['HTML5 Canvas', 'WebGL', 'Multiplayer', 'Cross-platform']
    },
    {
      icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'AR/VR Games',
      description: 'Immersive augmented and virtual reality',
      features: ['VR Headsets', 'AR Mobile', 'Motion Controls', 'Immersive']
    }
  ];

  const gameEngines = [
    { name: 'Unity', icon: '🎮', color: 'from-gray-700 to-black', platforms: ['iOS', 'Android', 'PC', 'Console'] },
    { name: 'Unreal Engine', icon: '⚡', color: 'from-blue-600 to-purple-600', platforms: ['PC', 'Console', 'VR'] },
    { name: 'Godot', icon: '🤖', color: 'from-blue-500 to-cyan-500', platforms: ['Multi-platform', '2D/3D'] },
    { name: 'Phaser', icon: '🌐', color: 'from-orange-500 to-red-500', platforms: ['HTML5', 'Browser'] }
  ];

  const gameTypes = [
    { type: 'Casual Games', desc: 'Easy to play', icon: '🎯' },
    { type: 'Puzzle Games', desc: 'Brain teasers', icon: '🧩' },
    { type: 'Action Games', desc: 'Fast-paced', icon: '⚔️' },
    { type: 'Racing Games', desc: 'High-speed', icon: '🏎️' },
    { type: 'Strategy Games', desc: 'Tactical', icon: '♟️' },
    { type: 'RPG Games', desc: 'Adventures', icon: '🗡️' },
    { type: 'Multiplayer', desc: 'Competitive', icon: '👥' },
    { type: 'Educational', desc: 'Learning', icon: '📚' }
  ];

  const technologies = [
    { name: 'Unity 3D', icon: '🎮' },
    { name: 'Unreal Engine', icon: '⚡' },
    { name: 'Blender', icon: '🎨' },
    { name: 'Photoshop', icon: '🖼️' },
    { name: 'C#', icon: '💻' },
    { name: 'C++', icon: '⚙️' }
  ];

  const features = [
    '2D & 3D Graphics', 'Physics Engine', 'AI & Pathfinding', 'Multiplayer Networking',
    'In-App Purchases', 'Analytics Integration', 'Social Features', 'Cloud Saves'
  ];

  const pricingPlans = [
    {
      name: 'Casual Game',
      price: 24999,
      monthlySupport: 499,
      color: 'rose',
      badge: null,
      features: [
        '2D Mobile Game Development',
        '5-10 Game Levels',
        'Basic Game Features',
        'iOS & Android Release',
        '2 Months Support Included',
        'Store Optimization'
      ]
    },
    {
      name: 'Premium Game',
      price: 74999,
      monthlySupport: 1999,
      color: 'pink',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        '3D Game Development',
        'Multiplayer Networking',
        'Advanced Game Features',
        'Monetization Setup',
        '4 Months Support Included',
        'Game Analytics',
        'Post-Launch Updates'
      ]
    },
    {
      name: 'AAA Game',
      price: null,
      monthlySupport: 9999,
      color: 'purple',
      badge: null,
      features: [
        'Console-Quality Game',
        'Dedicated Development Team',
        'Advanced 3D Graphics & AI',
        'Cross-platform Release',
        '24/7 Priority Support',
        'Continuous Updates',
        'Marketing Support'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-rose-600 via-pink-600 to-purple-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                Game Development Services
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-rose-100 mb-6 sm:mb-8 leading-relaxed">
                Create engaging games that players love. Expert development for mobile, PC, console, and VR/AR platforms.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-rose-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start Game Project
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Game Portfolio
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&h=400&fit=crop" alt="Game Development" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Game Development Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Full-service game development from concept to launch and beyond
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-rose-100 dark:bg-rose-900/30 rounded-lg flex items-center justify-center text-rose-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Vision Section - Future Focused */}
      <section className="py-12 sm:py-20 lg:py-24 bg-gradient-to-br from-rose-600/10 via-pink-600/10 to-purple-600/10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl lg:max-w-7xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-12 shadow-2xl border border-rose-200/50 dark:border-rose-800/50">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 lg:gap-12">
              {/* Left Content - Vision & Process */}
              <div className="lg:w-1/2">
                <div className="text-center lg:text-left">
                  <Gamepad2 className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-rose-600 mx-auto lg:mx-0 mb-4 lg:mb-6 opacity-75 animate-pulse" />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-4 lg:mb-6">
                    We'll Build Your Game Vision
                  </h2>
                  <p className="text-base sm:text-lg lg:text-xl text-gray-700 dark:text-gray-300 mb-6 lg:mb-8 leading-relaxed">
                    Share your game idea, and we'll bring it to life with expert development, stunning visuals, and engaging gameplay. 
                    Whether it's a casual mobile game or an immersive AAA experience, 
                    <strong className="font-semibold text-rose-600 dark:text-rose-400 block lg:inline"> we'll build exactly what you envision</strong>. 
                    Your dream game → Our development expertise.
                  </p>
                </div>

                {/* Development Process - Desktop */}
                <div className="hidden lg:grid grid-cols-2 gap-6 mb-8">
                  <div className="group p-6 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-2xl border border-rose-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Smartphone className="w-12 h-12 text-rose-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Mobile Games</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">iOS & Android</div>
                          <div className="text-lg font-bold text-pink-600">Casual to Core</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="group p-6 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-2xl border border-rose-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Zap className="w-12 h-12 text-rose-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">PC & Console</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">High-performance</div>
                          <div className="text-lg font-bold text-pink-600">2D to 3D</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop CTA Buttons */}
                <div className="hidden lg:flex flex-col gap-4">
                  <Link to="/contact" className="px-8 py-4 bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 text-white rounded-2xl font-black text-lg shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    🎮 Start Your Game Project
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/portfolio" className="px-8 py-4 border-2 border-rose-600 text-rose-600 dark:text-rose-400 rounded-2xl font-black text-lg hover:bg-rose-600 hover:text-white transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    View Our Capabilities
                    <ExternalLink className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              {/* Right Content - Game Types We Can Build */}
              <div className="lg:w-1/2">
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-2xl border border-rose-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <Users className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-rose-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">Puzzle & Strategy</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-rose-600">Brain-teasing gameplay</div>
                      <div className="text-base sm:text-lg font-medium text-pink-600">Engaging mechanics</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-2xl border border-rose-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <Monitor className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-rose-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">Action & Adventure</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-rose-600">Immersive worlds</div>
                      <div className="text-base sm:text-lg font-medium text-pink-600">Fast-paced action</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-2xl border border-rose-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 lg:col-span-2">
                    <div className="flex flex-col sm:flex-row items-center gap-4 lg:gap-6">
                      <div className="text-4xl">🛠️</div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2">Custom Development</h3>
                        <div className="text-xl sm:text-2xl lg:text-2xl font-bold text-rose-600">Your Vision, Our Expertise</div>
                        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">Tell us what you want, we'll build it</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Development Process - Mobile */}
                <div className="mt-6 lg:hidden">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="group p-4 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-xl border border-rose-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Concept</div>
                        <div className="text-sm text-rose-600">Ideation & Planning</div>
                      </div>
                    </div>
                    <div className="group p-4 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-xl border border-rose-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Development</div>
                        <div className="text-sm text-rose-600">Coding & Design</div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile/Tablet CTA Buttons */}
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <Link to="/contact" className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 text-white rounded-2xl font-black text-sm sm:text-base shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide">
                      🎮 Start Your Game Project
                    </Link>
                    <Link to="/portfolio" className="px-6 sm:px-8 py-3 sm:py-4 border-2 sm:border-4 border-rose-600 text-rose-600 dark:text-rose-400 rounded-2xl font-black text-sm sm:text-base hover:bg-rose-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                      View Our Capabilities
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Game Development Capabilities - Desktop Only */}
            <div className="hidden lg:grid grid-cols-4 gap-4 mt-12 pt-8 border-t border-rose-200/50 dark:border-rose-800/50">
              {[
                { label: 'Game Genres', value: '10+', icon: '🎮', description: 'Different types' },
                { label: 'Platforms', value: '8+', icon: '📱', description: 'iOS to Console' },
                { label: 'Development', value: 'End-to-End', icon: '⚡', description: 'Full service' },
                { label: 'Team Size', value: 'Scalable', icon: '👥', description: 'Flexible teams' }
              ].map((capability, index) => (
                <div key={index} className="text-center p-4 bg-gradient-to-br from-rose-50/50 to-pink-50/50 dark:from-rose-900/20 dark:to-pink-900/20 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="text-3xl mb-2">{capability.icon}</div>
                  <div className="text-2xl font-black text-rose-600">{capability.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{capability.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">{capability.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Game Engines */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Game Engines We Master
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Industry-leading game engines for any project
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {gameEngines.map((engine, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all group border border-gray-200/50 dark:border-gray-700/50">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${engine.color} rounded-lg flex items-center justify-center text-3xl sm:text-4xl text-white mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform`}>
                  {engine.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4 text-gray-900 dark:text-white">{engine.name}</h3>
                <div className="space-y-2">
                  {engine.platforms.map((platform, idx) => (
                    <div key={idx} className="bg-rose-50 dark:bg-rose-900/30 px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm text-center text-rose-700 dark:text-rose-300 border border-rose-200/50 dark:border-rose-700/50 font-medium">
                      {platform}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Types */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Types of Games We Develop
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              From casual to hardcore gaming experiences
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {gameTypes.map((game, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all group hover:-translate-y-2">
                <div className="text-4xl sm:text-5xl mb-2 sm:mb-3 text-center group-hover:scale-110 transition-transform">{game.icon}</div>
                <h3 className="text-base sm:text-lg font-bold text-center mb-1 sm:mb-2 text-gray-900 dark:text-white">{game.type}</h3>
                <p className="text-xs sm:text-sm text-center text-gray-600 dark:text-gray-400">{game.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Technologies & Tools
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg text-center hover:scale-105 transition-all border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-3xl sm:text-5xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{tech.icon}</div>
                <h3 className="font-bold text-xs sm:text-base text-gray-900 dark:text-white">{tech.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Features */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Game Features We Implement
          </h2>
          <div className="grid md:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-3 sm:p-6 shadow-lg border border-gray-200/50 dark:border-gray-700/50 text-center hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:scale-105 transition-all duration-300 group">
                <p className="text-xs sm:text-base text-gray-900 dark:text-white font-bold group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              Game Development Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Transparent pricing for game development. From casual to AAA games.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                rose: { bg: 'from-rose-600 to-pink-600', light: 'from-rose-50 to-pink-50', hover: 'hover:border-rose-200' },
                pink: { bg: 'from-pink-600 to-purple-600', light: 'from-pink-50 to-purple-50', hover: 'hover:border-pink-200' },
                purple: { bg: 'from-purple-600 to-indigo-600', light: 'from-purple-50 to-indigo-50', hover: 'hover:border-purple-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-pink-500 dark:border-pink-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-purple-500/10 dark:from-pink-600/20 dark:to-purple-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'Casual Game' && 'Perfect to test your concept'}
                    {plan.name === 'Premium Game' && 'Best for serious game launches'}
                    {plan.name === 'AAA Game' && 'Console-quality games'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'rose' ? '#e11d48, #ec4899' : plan.color === 'pink' ? '#ec4899, #9333ea' : '#9333ea, #6366f1'})`}}>
                            ${plan.price.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'rose' ? '#e11d48, #ec4899' : plan.color === 'pink' ? '#ec4899, #9333ea' : '#9333ea, #6366f1'})`}}>
                            ${plan.monthlySupport.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'rose' ? '#e11d48, #ec4899' : plan.color === 'pink' ? '#ec4899, #9333ea' : '#9333ea, #6366f1'})`}}>
                          ${plan.monthlySupport.toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'rose' ? '#ffe4e6' : plan.color === 'pink' ? '#f3e8ff' : '#e0e7ff'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'rose' ? '#e11d48' : plan.color === 'pink' ? '#9333ea' : '#6366f1'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'AAA Game' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '🎮', text: 'Game Design' },
                { icon: '🎨', text: 'Custom Art Assets' },
                { icon: '📱', text: 'Multi-platform Build' },
                { icon: '🚀', text: 'Store Submission' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 rounded-2xl border border-rose-200/50 dark:border-rose-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Game Development FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How long does game development take?',
                  a: 'Casual games: 4-8 weeks. Premium games: 4-6 months. AAA games: 1-2+ years. Timeline depends on complexity, features, and platforms.'
                },
                {
                  q: 'What platforms can you develop for?',
                  a: 'iOS, Android, Windows, macOS, Linux, PlayStation, Xbox, Nintendo Switch, VR/AR, and web browsers. We handle cross-platform development and optimization.'
                },
                {
                  q: 'Do you handle game art and sound?',
                  a: 'Yes! We have in-house artists, animators, and audio specialists. Or we can integrate with your preferred creative team.'
                },
                {
                  q: 'Can you help with game monetization?',
                  a: 'Absolutely! We implement ads, in-app purchases, battle passes, cosmetics, and other monetization strategies based on your game type.'
                },
                {
                  q: 'What about post-launch support?',
                  a: 'All plans include support for bug fixes, updates, new features, and platform changes. AAA games receive ongoing maintenance and content updates.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Gamepad2 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-bounce" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Turn Your Game Idea Into Reality
          </h2>
          <p className="text-base sm:text-xl text-rose-100 mb-6 sm:mb-8 px-2">
            Partner with experienced game developers. Let's create the next hit game together and dominate the app stores.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-rose-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>🎮 Schedule Game Consultation</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="game-development" />
      </div>
    </div>
  );
}